import { useCallback, useMemo, useRef } from 'react';

import { IModifier, IModifierData } from 'features/menu/components/product-detail/modifier/types';
import {
  getValidModifiersForSelection,
  mapModifierForCustomization,
} from 'features/menu/components/product-detail/modifier/utils';
import {
  GroupsCustomizationModifiersContainer,
  ModifiersCustomizationContainer,
} from 'features/menu/components/product-detail/product-customization/styled';
import {
  ComboSlotType,
  SelectModifierFn,
} from 'features/menu/components/product-detail/product-customization/types';
import { useProductWizardContext } from 'features/menu/components/product-wizard';
import {
  DEFAULT_SLOT_KEY,
  MultipliersSelections,
  SlotSelections,
} from 'features/menu/components/product-wizard/types';
import { useProductWizardFlow } from 'features/menu/components/product-wizard/use-product-wizard-flow';

import { ListCardGroup } from 'components/list-card-group';
import { FlatCustomizationHeader } from 'components/product-customization/flat-customization-header';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { hasOptionEntries } from 'state/unified-menu/utils';

import { Modifier } from '../modifier/modifier';

import { IModifierGroup, ModifierGroup } from './product-customization-modifier-group';
import {
  getComboSlotType,
  groupModifiersByDisplayName,
  stabilizeModifierGroupSelectionsOneMenu,
} from './utils';

export const ProductCustomizationItemModifiers = ({
  onModifierSelectionChange: onModifierSelectionChangeProp,
  hasMultipleComboSlotOptions = false,
  forceGroupModifiers = false,
}: {
  hasMultipleComboSlotOptions?: boolean;
  onModifierSelectionChange?: SelectModifierFn;
  forceGroupModifiers?: boolean;
}) => {
  const { posServiceMode } = useMenuQueryOptions();
  const { indexedMenu } = useUnifiedMenuContext();
  const { slotsSelections, selectModifierMultiplier } = useProductWizardContext();
  const { currentFlowStep } = useProductWizardFlow();

  const slotKey = currentFlowStep.slotKey || DEFAULT_SLOT_KEY;
  const slotSelections = slotsSelections[slotKey];
  const selectedItemId = slotSelections?.itemId;
  const shouldGroupModifiers =
    forceGroupModifiers ||
    slotKey === DEFAULT_SLOT_KEY ||
    getComboSlotType(indexedMenu, slotKey) === ComboSlotType.PICKER_ASPECT;

  const modifiers = useMemo(() => {
    if (!selectedItemId) {
      return [];
    }

    const selectedItem = indexedMenu.get(selectedItemId);
    if (!hasOptionEntries(posServiceMode, selectedItem)) {
      return [];
    }

    const mappedModifiers = selectedItem.options.entries.map(({ entryId, quantity }) =>
      mapModifierForCustomization(indexedMenu, entryId, posServiceMode, quantity)
    );

    // filter if null values
    const filteredMods: IModifier[] = (mappedModifiers as Array<IModifier | null>).filter(
      (modifier): modifier is IModifier => !!modifier && !!modifier.multipliers?.length
    );

    const validModifiers = getValidModifiersForSelection(filteredMods);
    return validModifiers;
  }, [indexedMenu, posServiceMode, selectedItemId]);

  const onModifierSelectionChange = useCallback<SelectModifierFn>(
    (selections, modifier) => {
      onModifierSelectionChangeProp?.(selections, modifier);

      if (selectedItemId) {
        selectModifierMultiplier(slotKey, selectedItemId, modifier.id, selections);
      }
    },
    [onModifierSelectionChangeProp, selectModifierMultiplier, selectedItemId, slotKey]
  );

  if (modifiers.length === 0) {
    return null;
  }
  const Component =
    shouldGroupModifiers && !hasMultipleComboSlotOptions
      ? GroupsCustomizationModifiers
      : FlatCustomizationModifiers;

  return (
    <Component
      modifiers={modifiers}
      slotSelections={slotSelections}
      onModifierSelectionChange={onModifierSelectionChange}
      hasMultipleComboSlotOptions={hasMultipleComboSlotOptions}
    />
  );
};

const GroupsCustomizationModifiers = ({
  modifiers,
  slotSelections,
  onModifierSelectionChange,
  isFullContainer = false,
}: {
  modifiers: IModifier[];
  slotSelections: SlotSelections | undefined;
  isFullContainer?: boolean;
  onModifierSelectionChange: SelectModifierFn;
}) => {
  const selectionsCacheRef = useRef(new Map<string, MultipliersSelections>());

  const modifierGroupsProps = useMemo<IModifierGroup[]>(() => {
    const modifierSelections = slotSelections?.modifiers ?? {};
    const groups = groupModifiersByDisplayName(modifiers, modifierSelections);
    return stabilizeModifierGroupSelectionsOneMenu(groups, selectionsCacheRef.current);
  }, [modifiers, slotSelections?.modifiers]);

  const isMobile = useIsMobileBreakpoint();

  return (
    <GroupsCustomizationModifiersContainer>
      {modifierGroupsProps.map(modifierGroupProps =>
        modifierGroupProps.modifiers.length ? (
          <ModifierGroup
            {...modifierGroupProps}
            key={modifierGroupProps.key}
            isFullContainer={isFullContainer || isMobile}
            onModifierSelectionChange={onModifierSelectionChange}
          />
        ) : null
      )}
    </GroupsCustomizationModifiersContainer>
  );
};

const FlatCustomizationModifiers = ({
  modifiers,
  slotSelections,
  onModifierSelectionChange,
  hasMultipleComboSlotOptions = false,
}: {
  modifiers: IModifier[];
  slotSelections: SlotSelections | undefined;
  onModifierSelectionChange: SelectModifierFn;
  hasMultipleComboSlotOptions?: boolean;
}) => {
  const isMobile = useIsMobileBreakpoint();

  const modifiersData = useMemo<IModifierData[]>(() => {
    const modifierSelections = slotSelections?.modifiers ?? {};
    return modifiers.map(modifier => ({
      modifier,
      selections: modifierSelections[modifier.id] || {},
    }));
  }, [modifiers, slotSelections?.modifiers]);

  if (hasMultipleComboSlotOptions) {
    return (
      <ModifiersCustomizationContainer>
        <FlatCustomizationHeader />
        <GroupsCustomizationModifiers
          modifiers={modifiers}
          slotSelections={slotSelections}
          onModifierSelectionChange={onModifierSelectionChange}
          isFullContainer
        />
      </ModifiersCustomizationContainer>
    );
  }

  return (
    <ModifiersCustomizationContainer>
      <ListCardGroup
        isFullContainer={isMobile}
        items={modifiersData}
        header={<FlatCustomizationHeader />}
        renderItem={({ item: modifierProps }: { item: IModifierData }) => {
          return (
            <Modifier
              key={`modifier-${modifierProps.modifier.id}`}
              {...modifierProps}
              onSelectionsChange={selections => {
                onModifierSelectionChange(selections, modifierProps.modifier);
              }}
            />
          );
        }}
      />
    </ModifiersCustomizationContainer>
  );
};
